import {
  AnimatePresence,
  Badge,
  BadgeVariantEnum,
  ChevronDownIcon,
  EditIcon,
  FontSizeEnum,
  FontWeightEnum,
  IconButtonColorEnum,
  IconButtonSection,
  IconButtonSectionInternalProps,
  IconButtonSectionSizeEnum,
  IconSizeEnum,
  motion,
  SupportRadioIcon,
  SupportTvIcon,
  TextOrientationEnum,
  Typography,
} from '@acceso/design-system';
import clsx from 'clsx';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ItemList } from '@/components/ItemList';
import { RenderEnrichementsIcons } from '@/components/RenderEnrichementsIcons/RenderEnrichementsIcons';
import { StateActiveInTable } from '@/components/StateActiveInTable/StateActiveInTable';
import { UserAction } from '@/components/UserAction';
import { PRIORITY } from '@/features/broadcast-schedule/constants';
import { ChannelMediaTypeEnum } from '@/features/broadcasters/types';
import { useLanguages } from '@/features/languages/api/get-languages';
import { useGetProgramsCategories } from '@/features/programs/api/get-programs-categories';
import { useGetProgramsClassifications } from '@/features/programs/api/get-programs-classifications';
import { useGetProgramsViewedPlaces } from '@/features/programs/api/get-programs-viewed-places';
import { TProgram } from '@/features/programs/types';
import { formatDateCustom } from '@/helpers/format-date';
import { getScheduleDays } from '@/utils/date';
import { useCurrentLanguage } from '@/utils/i18n.utils';

const internalPropsIconButtonEditUser: IconButtonSectionInternalProps = {
  icon: {
    type: EditIcon,
  },
  iconButton: {
    color: IconButtonColorEnum.NONE_DEFAULT,
  },
  text: {
    value: 'Editar',
    orientation: TextOrientationEnum.LEFT,
    className: 'text-text-dark-10 text-body-3 font-regular',
  },
};

type ProgramDetailsProps = {
  program: TProgram;
  onUpdate: (program: TProgram) => void;
};

export function ProgramDetail({ program, onUpdate }: ProgramDetailsProps) {
  const { t } = useTranslation();
  const currentLanguage = useCurrentLanguage();
  const { data: languages = [] } = useLanguages();
  const { data: categories = [] } = useGetProgramsCategories();
  const { data: classifications = [] } = useGetProgramsClassifications();
  const { data: viewedPlaces = [] } = useGetProgramsViewedPlaces();

  const [isScheduleOpen, setIsScheduleOpen] = useState(false);

  const programLanguage = useMemo(
    () => languages.find(language => language.id === program.language_id),
    [languages, program]
  );

  const programVisionLanguage = useMemo(
    () => languages.find(language => language.id === program.language_transcription_id),
    [languages, program]
  );

  const programCategory = useMemo(
    () => categories.find(category => category.id === program.program_category_id),
    [categories, program]
  );

  const programClassification = useMemo(
    () =>
      classifications.find(
        classification => classification.value === program.program_classification
      ),
    [classifications, program]
  );

  const programViewedPlace = useMemo(
    () => viewedPlaces.find(viewedPlace => viewedPlace.id === program.program_viewed_place_id),
    [viewedPlaces, program]
  );

  return (
    <div className="flex flex-1 flex-col items-start self-stretch">
      <div className="flex items-start justify-end self-stretch">
        <IconButtonSection
          onClick={() => onUpdate(program)}
          internalProps={internalPropsIconButtonEditUser}
          size={IconButtonSectionSizeEnum.LG}
        />
      </div>
      <div className="flex w-full flex-col divide-y divide-stroke-20">
        <ItemList title={t('Descripción')}>
          <Typography fontSize={FontSizeEnum.Basic} weight={FontWeightEnum.Semibold}>
            {program.description}
          </Typography>
        </ItemList>
        <ItemList title={t('ID AV (Legacy)')}>
          <Typography fontSize={FontSizeEnum.Xs}>{program.legacy_id}</Typography>
        </ItemList>
        <ItemList title={t('Categoría y clasificación')}>
          <div className="flex flex-col gap-1">
            <Typography fontSize={FontSizeEnum.Basic} weight={FontWeightEnum.Semibold}>
              {programCategory?.name}
            </Typography>
            <Typography fontSize={FontSizeEnum.Basic} className="capitalize">
              {programClassification?.labels[currentLanguage]}
            </Typography>
          </div>
        </ItemList>
        <ItemList title={t('Rango de fechas')}>
          <Typography fontSize={FontSizeEnum.Basic}>
            {formatDateCustom({ input: program.start_datetime, format: 'dd/MM/yyyy' })} -{' '}
            {formatDateCustom({ input: program.end_datetime, format: 'dd/MM/yyyy' })}
          </Typography>
        </ItemList>
        <ItemList title={t('Estado')}>
          <StateActiveInTable status={program.state} />
        </ItemList>
        <ItemList title={t('Idiomas')}>
          <div className="flex flex-col gap-1">
            <Typography fontSize={FontSizeEnum.Basic}>
              {t('Emisión')}: {programLanguage?.name}
            </Typography>
            <Typography fontSize={FontSizeEnum.Basic}>
              {t('Visionado')}: {programVisionLanguage?.name}
            </Typography>
          </div>
        </ItemList>
        <ItemList title={t('Emisora')}>
          <div className="flex items-center gap-2">
            {program.channel.media_type === ChannelMediaTypeEnum.TV ? (
              <SupportTvIcon />
            ) : (
              <SupportRadioIcon />
            )}
            <div className="flex flex-col">
              <Typography fontSize={FontSizeEnum.Basic} weight={FontWeightEnum.Semibold}>
                {program.channel.name}
              </Typography>
              <Typography fontSize={FontSizeEnum.Basic}>{program.channel.short_name}</Typography>
            </div>
          </div>
        </ItemList>
        <ItemList title={t('Prioridad')}> {PRIORITY[program.priority - 1].render} </ItemList>
        <ItemList title={t('Lugar de visionado')}>
          <Typography fontSize={FontSizeEnum.Basic}>{programViewedPlace?.name}</Typography>
        </ItemList>
        <ItemList title={t('Programación')}>
          <div className="flex w-full items-center justify-between gap-size-8">
            <Typography fontSize={FontSizeEnum.Xs} weight={FontWeightEnum.Semibold}>
              {program.schedule.length > 0
                ? `${getScheduleDays(program.schedule[0].repetition_day_of_week)} ${program.schedule[0].start_hour} - ${program.schedule[0].end_hour}`
                : ''}
            </Typography>

            {program.schedule.length > 1 && (
              <div className="flex items-center gap-2">
                <Badge variant={BadgeVariantEnum.DefaultOutline}>
                  {`+${program.schedule.length - 1}`}
                </Badge>

                <IconButtonSection
                  onClick={() => setIsScheduleOpen(state => !state)}
                  className={clsx(
                    'transition-transform duration-300',
                    isScheduleOpen ? 'rotate-180' : 'rotate-0'
                  )}
                  size={IconButtonSectionSizeEnum.LG}
                  internalProps={{
                    icon: { type: ChevronDownIcon, size: IconSizeEnum.MD },
                    text: { value: '' },
                    iconButton: {
                      color: IconButtonColorEnum.NONE_DEFAULT,
                    },
                  }}
                />
              </div>
            )}
          </div>
        </ItemList>

        {program.schedule.length >= 2 && (
          <AnimatePresence>
            {isScheduleOpen && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.5, ease: 'easeInOut' }}
                className="flex w-full flex-col justify-start gap-1 py-2"
              >
                {program.schedule.slice(1).map(schedule => (
                  <Typography
                    key={schedule.id}
                    fontSize={FontSizeEnum.Xs}
                    weight={FontWeightEnum.Semibold}
                    className="ml-[46%] w-1/2"
                  >
                    {`${getScheduleDays(schedule.repetition_day_of_week)} ${schedule.start_hour} - ${schedule.end_hour}`}
                  </Typography>
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        )}
        <ItemList title={t('Enriquecimientos')}>
          <RenderEnrichementsIcons
            classname="items-center"
            iconClassname="text-text-dark-10"
            enrichments={{
              ocr: program.parameters?.enrichments?.ocr,
              stt: program.parameters?.enrichments?.stt,
              logos: program.parameters?.enrichments?.logos,
              peoples: program.parameters?.enrichments?.peoples,
              advertising: program.parameters?.enrichments?.advertising,
            }}
          />
        </ItemList>
        <ItemList title={t('Creado por')}>
          <UserAction
            id={program?.created_by || new Date().toISOString()}
            timestamp={program?.created_at || new Date().toISOString()}
          />
        </ItemList>
        <ItemList title={t('Actualizado por')}>
          <UserAction
            id={program?.created_by || new Date().toISOString()}
            timestamp={program?.created_at || new Date().toISOString()}
          />
        </ItemList>
      </div>
    </div>
  );
}
